/**
 * Dashboard aggregation primitives — range-aware.
 *
 * Pure functions — no Preact, no signals, no IO. Every average and
 * percentage normalizes against working days only (utils/workdays
 * excludes weekends, Italian public holidays, admin-flagged closures).
 *
 * The Dashboard's date range picker (which can span 1 to N months)
 * builds a unified DayCell[] across the range and passes it to these
 * functions. The single-month case is just a 1-element range so the
 * math stays uniform.
 *
 * "In office" definition: P + M + V (Lodi presence + Milan office +
 * Rovereto Office). All three are physical-presence statuses, so they
 * contribute to occupancy / in-office metrics together. Remote (R),
 * Trip (T), Available-but-not-at-desk (A), and Out (O) do not.
 */
import { dateKey } from "../utils/dates";
import { getHoliday } from "../utils/holidays";
import { workdaysOfMonth, monthsInRange } from "../utils/workdays";
import type {
  CalendarMap,
  ClosureMap,
  RosterMap,
  StatusCode,
} from "../types";

export const STATUS_CODES: StatusCode[] = ["P", "T", "R", "M", "V", "A", "O"];

/** The subset of statuses that count as "in office" presence. P=Lodi
 *  (default office), M=Milan, V=Rovereto. Used by every occupancy /
 *  in-office calculation so adding a future office is a one-line
 *  change here. */
export const IN_OFFICE_CODES: ReadonlyArray<StatusCode> = ["P", "M", "V"];

function emptyCounts(): Record<StatusCode, number> {
  return { P: 0, T: 0, R: 0, M: 0, O: 0, A: 0, V: 0 };
}

function sumInOffice(counts: Record<StatusCode, number>): number {
  return IN_OFFICE_CODES.reduce((acc, c) => acc + counts[c], 0);
}

export interface DayCell {
  day: number;       // 1..31 (day-of-month — meaningful only with .year/.month)
  year: number;
  month: number;     // 0-indexed
  key: string;       // YYYY-MM-DD
  isWorkday: boolean;
  isWeekend: boolean;
  holidayName: string | null;
  closureLabel: string | null;
  counts: Record<StatusCode, number>;
  filled: number;
  inOffice: number;  // P + M + V
}

/** Per-day breakdown for a single month. Non-working days kept in the
 *  result with `isWorkday: false` so the daily chart can mute them. */
export function computeDays(
  year: number,
  month: number,
  people: string[],
  calendar: CalendarMap,
  closures: ClosureMap,
): DayCell[] {
  const n = new Date(year, month + 1, 0).getDate();
  const workdaySet = new Set(workdaysOfMonth(year, month, closures));
  const days: DayCell[] = [];
  for (let d = 1; d <= n; d++) {
    const key = dateKey(year, month, d);
    const counts = emptyCounts();
    people.forEach((name) => {
      const c = calendar[name]?.[key];
      if (c && c in counts) counts[c as StatusCode]++;
    });
    const filled = STATUS_CODES.reduce((acc, c) => acc + counts[c], 0);
    const dow = new Date(year, month, d).getDay();
    const isWE = dow === 0 || dow === 6;
    const holiday = getHoliday(key);
    const closure = closures[key];
    days.push({
      day: d,
      year,
      month,
      key,
      isWorkday: workdaySet.has(d),
      isWeekend: isWE,
      holidayName: holiday?.name ?? null,
      closureLabel:
        closure !== undefined ? (closure || "Closure") : null,
      counts,
      filled,
      inOffice: sumInOffice(counts),
    });
  }
  return days;
}

/** Days across a range — concatenation of per-month results. Useful for
 *  the Dashboard's range picker (e.g. "Jan–Mar 2026"). For a single-
 *  month range, behaves identically to computeDays. */
export function computeDaysInRange(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
  people: string[],
  calendar: CalendarMap,
  closures: ClosureMap,
): DayCell[] {
  const months = monthsInRange(startYear, startMonth, endYear, endMonth);
  const out: DayCell[] = [];
  months.forEach(({ year, month }) => {
    out.push(...computeDays(year, month, people, calendar, closures));
  });
  return out;
}

export interface RangeAggregate {
  workingDays: number;
  totalPeople: number;
  expectedCells: number; // workingDays × totalPeople
  filledCells: number;
  coverage: number;       // 0..1
  statusTotals: Record<StatusCode, number>;
  statusShare: Record<StatusCode, number>;
  avgInOffice: number;    // bodies (P+M+V) per working day
  avgOccupancy: number;   // fraction of total people (0..1)
  avgRemoteShare: number;
  avgTripShare: number;
  peak: { day: number; year: number; month: number; key: string; count: number } | null;
}

/** Sum across days (single-month or range) to produce a unified
 *  aggregate. Working-day filter applied inside. */
export function computeRangeAggregate(
  days: DayCell[],
  totalPeople: number,
): RangeAggregate {
  const workdays = days.filter((d) => d.isWorkday);
  const statusTotals = emptyCounts();
  let inOfficeTotal = 0;
  let peak: RangeAggregate["peak"] = null;
  workdays.forEach((d) => {
    STATUS_CODES.forEach((c) => {
      statusTotals[c] += d.counts[c];
    });
    inOfficeTotal += d.inOffice;
    if (!peak || d.inOffice > peak.count) {
      peak = {
        day: d.day,
        year: d.year,
        month: d.month,
        key: d.key,
        count: d.inOffice,
      };
    }
  });
  const filledCells = STATUS_CODES.reduce(
    (acc, c) => acc + statusTotals[c],
    0,
  );
  const expectedCells = workdays.length * totalPeople;
  const coverage = expectedCells > 0 ? filledCells / expectedCells : 0;
  const statusShare: Record<StatusCode, number> = emptyCounts();
  if (expectedCells > 0) {
    STATUS_CODES.forEach((c) => {
      statusShare[c] = statusTotals[c] / expectedCells;
    });
  }
  const avgInOffice =
    workdays.length > 0 ? inOfficeTotal / workdays.length : 0;
  const avgOccupancy = totalPeople > 0 ? avgInOffice / totalPeople : 0;
  return {
    workingDays: workdays.length,
    totalPeople,
    expectedCells,
    filledCells,
    coverage,
    statusTotals,
    statusShare,
    avgInOffice,
    avgOccupancy,
    avgRemoteShare: statusShare.R,
    avgTripShare: statusShare.T,
    peak,
  };
}

export interface DayOfWeekStat {
  wd: number; // 1..5 (Mon..Fri)
  label: string;
  workingDays: number; // how many of this DoW were workdays in the range
  avgInOffice: number; // bodies (P+M+V), avg across that DoW's workdays
  avgOccupancy: number; // fraction of total people (0..1)
}

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export function computeDayOfWeek(
  days: DayCell[],
  totalPeople: number,
): DayOfWeekStat[] {
  return DOW_LABELS.map((label, idx) => {
    const target = idx + 1; // Mon=1..Fri=5
    let inOfficeSum = 0;
    let workingDays = 0;
    days.forEach((d) => {
      if (!d.isWorkday) return;
      if (new Date(d.year, d.month, d.day).getDay() !== target) return;
      inOfficeSum += d.inOffice;
      workingDays++;
    });
    const avgInOffice =
      workingDays > 0 ? inOfficeSum / workingDays : 0;
    return {
      wd: target,
      label,
      workingDays,
      avgInOffice,
      avgOccupancy: totalPeople > 0 ? avgInOffice / totalPeople : 0,
    };
  });
}

export interface UnitStat {
  unit: string;
  headcount: number;
  expectedCells: number;
  filledCells: number;
  inOffice: number;   // P + M + V
  presence: number;   // P only
  milan: number;      // M only
  rovereto: number;   // V only
  remote: number;     // R
  trip: number;       // T
  out: number;        // O
  available: number;  // A
  coverage: number;
  inOfficeShare: number;
  remoteShare: number;
  tripShare: number;
}

/** Per-unit aggregation across a list of workday keys. The keys are
 *  pre-filtered to exclude weekends, holidays, closures — so the
 *  expectedCells denominator already reflects "working days only". */
export function computeUnits(
  workdayKeys: string[],
  people: string[],
  roster: RosterMap,
  calendar: CalendarMap,
): UnitStat[] {
  const map = new Map<string, UnitStat>();
  const get = (unit: string): UnitStat => {
    let u = map.get(unit);
    if (!u) {
      u = {
        unit,
        headcount: 0,
        expectedCells: 0,
        filledCells: 0,
        inOffice: 0,
        presence: 0,
        milan: 0,
        rovereto: 0,
        remote: 0,
        trip: 0,
        out: 0,
        available: 0,
        coverage: 0,
        inOfficeShare: 0,
        remoteShare: 0,
        tripShare: 0,
      };
      map.set(unit, u);
    }
    return u;
  };
  people.forEach((name) => {
    const u = get(roster[name]?.unit || "(no unit)");
    u.headcount++;
    const cal = calendar[name] ?? {};
    workdayKeys.forEach((k) => {
      const c = cal[k];
      if (!c) return;
      u.filledCells++;
      if (c === "P") {
        u.presence++;
        u.inOffice++;
      } else if (c === "M") {
        u.milan++;
        u.inOffice++;
      } else if (c === "V") {
        u.rovereto++;
        u.inOffice++;
      } else if (c === "R") u.remote++;
      else if (c === "T") u.trip++;
      else if (c === "O") u.out++;
      else if (c === "A") u.available++;
    });
  });
  const list = [...map.values()];
  list.forEach((u) => {
    u.expectedCells = u.headcount * workdayKeys.length;
    if (u.expectedCells > 0) {
      u.coverage = u.filledCells / u.expectedCells;
      u.inOfficeShare = u.inOffice / u.expectedCells;
      u.remoteShare = u.remote / u.expectedCells;
      u.tripShare = u.trip / u.expectedCells;
    }
  });
  return list.sort((a, b) => b.inOfficeShare - a.inOfficeShare);
}

export interface PersonStat {
  name: string;
  unit: string;
  role: string;
  email: string;
  workingDays: number;
  filled: number;
  presence: number;
  milan: number;
  rovereto: number;
  inOffice: number; // P + M + V
  remote: number;
  trip: number;
  out: number;
  available: number;
  coverage: number;
  inOfficeShare: number;
  remoteShare: number;
  tripShare: number;
  outShare: number;
  availableShare: number;
  roveretoShare: number;
  dayOfWeek: DayOfWeekStat[];
}

/** Per-person aggregation over a list of workday keys. */
export function computePerson(
  person: string,
  workdayKeys: string[],
  roster: RosterMap,
  calendar: CalendarMap,
): PersonStat {
  const entry = roster[person];
  const cal = calendar[person] ?? {};
  const stat: PersonStat = {
    name: person,
    unit: entry?.unit ?? "(no unit)",
    role: entry?.role ?? "",
    email: entry?.email ?? "",
    workingDays: workdayKeys.length,
    filled: 0,
    presence: 0,
    milan: 0,
    rovereto: 0,
    inOffice: 0,
    remote: 0,
    trip: 0,
    out: 0,
    available: 0,
    coverage: 0,
    inOfficeShare: 0,
    remoteShare: 0,
    tripShare: 0,
    outShare: 0,
    availableShare: 0,
    roveretoShare: 0,
    dayOfWeek: [],
  };
  workdayKeys.forEach((k) => {
    const c = cal[k];
    if (!c) return;
    stat.filled++;
    if (c === "P") {
      stat.presence++;
      stat.inOffice++;
    } else if (c === "M") {
      stat.milan++;
      stat.inOffice++;
    } else if (c === "V") {
      stat.rovereto++;
      stat.inOffice++;
    } else if (c === "R") stat.remote++;
    else if (c === "T") stat.trip++;
    else if (c === "O") stat.out++;
    else if (c === "A") stat.available++;
  });
  if (workdayKeys.length > 0) {
    stat.coverage = stat.filled / workdayKeys.length;
    stat.inOfficeShare = stat.inOffice / workdayKeys.length;
    stat.remoteShare = stat.remote / workdayKeys.length;
    stat.tripShare = stat.trip / workdayKeys.length;
    stat.outShare = stat.out / workdayKeys.length;
    stat.availableShare = stat.available / workdayKeys.length;
    stat.roveretoShare = stat.rovereto / workdayKeys.length;
  }
  // Personal day-of-week pattern.
  stat.dayOfWeek = DOW_LABELS.map((label, idx) => {
    const target = idx + 1;
    let inOff = 0;
    let count = 0;
    workdayKeys.forEach((k) => {
      const [yy, mm, dd] = k.split("-").map(Number);
      const date = new Date(yy ?? 0, (mm ?? 1) - 1, dd ?? 1);
      if (date.getDay() !== target) return;
      count++;
      const c = cal[k];
      if (c === "P" || c === "M" || c === "V") inOff++;
    });
    return {
      wd: target,
      label,
      workingDays: count,
      avgInOffice: inOff,
      avgOccupancy: count > 0 ? inOff / count : 0,
    };
  });
  return stat;
}

/** Cohort helpers. */
export function findEmptyCalendars(
  workdayKeys: string[],
  people: string[],
  calendar: CalendarMap,
): string[] {
  return people.filter((name) => {
    const cal = calendar[name] ?? {};
    return !workdayKeys.some((k) => cal[k] !== undefined);
  });
}

export function findHeavyTrip(
  workdayKeys: string[],
  people: string[],
  calendar: CalendarMap,
  threshold: number,
): string[] {
  return people.filter((name) => {
    const cal = calendar[name] ?? {};
    let trips = 0;
    workdayKeys.forEach((k) => {
      if (cal[k] === "T") trips++;
    });
    return trips > threshold;
  });
}

export interface AttentionItem {
  severity: "info" | "warn";
  message: string;
}

export function computeAttention(
  agg: RangeAggregate,
  units: UnitStat[],
  dow: DayOfWeekStat[],
  emptyCalendarCount: number,
  heavyTripCount: number,
): AttentionItem[] {
  const items: AttentionItem[] = [];

  if (emptyCalendarCount > 0) {
    items.push({
      severity: "warn",
      message: `${emptyCalendarCount} ${emptyCalendarCount === 1 ? "person has" : "people have"} no entries on any working day in this range — nudge them to fill in their calendar.`,
    });
  }

  if (agg.coverage < 0.85 && agg.expectedCells > 0) {
    items.push({
      severity: "warn",
      message: `Schedule coverage is ${pct(agg.coverage)} across the selected range — short of the 85% threshold.`,
    });
  }

  const lowCov = units.filter(
    (u) => u.coverage < 0.7 && u.expectedCells > 0,
  );
  if (lowCov.length > 0) {
    const u = lowCov[0]!;
    items.push({
      severity: "warn",
      message: `${u.unit} has ${pct(u.coverage)} coverage vs ${pct(agg.coverage)} company-wide.`,
    });
  }

  if (heavyTripCount > 0) {
    items.push({
      severity: "info",
      message: `${heavyTripCount} ${heavyTripCount === 1 ? "person is" : "people are"} on trip > 5 working days in this range — possible wellbeing signal.`,
    });
  }

  if (dow.length === 5) {
    const peakStat = dow.reduce((a, b) =>
      a.avgOccupancy > b.avgOccupancy ? a : b,
    );
    const troughStat = dow.reduce((a, b) =>
      a.avgOccupancy < b.avgOccupancy ? a : b,
    );
    if (
      peakStat.avgOccupancy > 0 &&
      troughStat.avgOccupancy / peakStat.avgOccupancy < 0.5
    ) {
      const ratio =
        troughStat.avgOccupancy > 0
          ? peakStat.avgOccupancy / troughStat.avgOccupancy
          : 0;
      items.push({
        severity: "info",
        message:
          ratio > 0
            ? `${peakStat.label} occupancy is ~${ratio.toFixed(1)}× ${troughStat.label}'s — uneven hybrid pattern.`
            : `${peakStat.label} sees in-office presence, ${troughStat.label} sees almost none — very uneven hybrid pattern.`,
      });
    }
  }

  return items;
}

/** Helper: collect workday keys from a precomputed DayCell array. */
export function workdayKeysFromDays(days: DayCell[]): string[] {
  return days.filter((d) => d.isWorkday).map((d) => d.key);
}

function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}
